/**
 * WUU Select API loader for wuu-tabs-product-list.
 *
 * 1) POST custom API for ranked handles (pagination / filters)
 * 2) GET /products/{handle}?view=wuu-card for real _product-card HTML
 */

const DEFAULT_ENDPOINT = 'https://app.wuuselect.world/api/shopify/landing-for-you-products';

/** Card */
const CARD_VIEW = 'wuu-card';
const CARD_FETCH_CONCURRENCY = 4;

/** Set true to skip the real custom API during local testing. */
const MOCK_ENABLE = true;
const MOCK_PAYLOAD = {
  code: 0,
  message: 'ok',
  data: {
    handles: [
      'wuu-canary-the-ordinary-676382520101',
    ],
    page: 1,
    pageSize: 12,
    total: 2,
  },
};

/**
 * @typedef {object} WuuTabsRequestContext
 * @property {AbortSignal} [signal]
 */

/**
 * Loads the next API page into the panel grid.
 * Skips fully-filtered pages while more results remain.
 *
 * @param {HTMLElement} panel
 * @param {WuuTabsRequestContext} [ctx]
 * @returns {Promise<void>}
 */
export async function loadNext(panel, ctx = {}) {
  const grid = panel.querySelector('[data-wuu-tabs-grid]');
  if (!grid) {
    panel.dataset.hasMore = 'false';
    setEmptyVisible(panel, true);
    return;
  }

  let rendered = 0;

  try {
    while (panel.dataset.hasMore === 'true') {
      const api = getAPI(panel);
      if (!api) {
        panel.dataset.hasMore = 'false';
        break;
      }

      let payload;
      if (MOCK_ENABLE) {
        payload = MOCK_PAYLOAD;
      } else {
        const response = await fetch(api, {
          method: 'POST',
          credentials: 'omit',
          body: '{}',
          signal: ctx.signal,
          headers: {
            Accept: 'application/json'
          },
        });
        if (!response.ok) throw new Error(`API response error: ${response.status}`);
        payload = await response.json();
      }

      if (Number(payload?.code) !== 0) {
        throw new Error(`API business error: ${payload?.message ?? 'unknown'}`);
      }

      const handles = getHandles(payload);
      const page = Number(getValueForKey(payload, 'data.page') || panel.dataset.nextPage || 1);
      const pageSize = Number(getValueForKey(payload, 'data.pageSize') || panel.dataset.apiPageSize || 12);
      const total = Number(getValueForKey(payload, 'data.total') || 0);
      const hasMore = pageSize > 0 && page * pageSize < total;

      if (handles.length) {
        const appended = await loadProducts(grid, handles, page, ctx);
        if (appended) {
          setEmptyVisible(panel, false);
          rendered += appended;
        }
      }

      panel.dataset.nextPage = String(page + 1);
      panel.dataset.hasMore = hasMore ? 'true' : 'false';

      if (handles.length > 0 || !hasMore) break;
    }
  } catch (error) {
    // Let the dispatcher ignore aborted in-flight requests without flashing empty.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    panel.dataset.hasMore = 'false';
    if (!panel.querySelector('[data-wuu-tabs-product-item]')) {
      setEmptyVisible(panel, true);
    }
    throw error;
  }

  if (!rendered && !panel.querySelector('[data-wuu-tabs-product-item]')) {
    setEmptyVisible(panel, true);
    panel.dataset.hasMore = 'false';
  }
}

/**
 * Fetch Liquid card HTML for each handle and append in API order.
 *
 * @param {HTMLElement} grid
 * @param {string[]} handles
 * @param {number} page
 * @param {WuuTabsRequestContext} [ctx]
 * @returns {Promise<number>}
 */
async function loadProducts(grid, handles, page, ctx = {}) {
  const existingIds = new Set(
    [...grid.querySelectorAll('[data-product-id]')].map((item) => item.getAttribute('data-product-id'))
  );

  const items = await getTasks(handles, CARD_FETCH_CONCURRENCY, async (handle) => {
    try {
      return await loadProductItem(handle, page, ctx.signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      console.warn(`[wuu-tabs-api] card fetch failed for ${handle}`, error);
      return null;
    }
  });

  const fragment = document.createDocumentFragment();
  let appended = 0;

  for (const item of items) {
    if (!item) continue;
    const productId = item.getAttribute('data-product-id');
    if (productId && existingIds.has(productId)) continue;
    if (productId) existingIds.add(productId);
    fragment.append(item);
    appended += 1;
  }

  if (appended) grid.append(fragment);
  return appended;
}

/**
 * @param {string} handle
 * @param {number} page
 * @param {AbortSignal} [signal]
 * @returns {Promise<Element | null>}
 */
async function loadProductItem(handle, page, signal) {
  const url = new URL(`/products/${encodeURIComponent(handle)}`, window.location.origin);
  url.searchParams.set('view', CARD_VIEW);

  const response = await fetch(url.toString(), {
    signal,
    credentials: 'same-origin',
    headers: {
      Accept: 'text/html'
    },
  });
  if (!response.ok) {
    throw new Error(`Card response error: ${response.status}`);
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const item = doc.querySelector('[data-wuu-tabs-product-item]');
  if (!item) return null;

  item.setAttribute('data-page', String(page));
  return item;
}

/**
 * Run async work over items with a concurrency limit, preserving order.
 *
 * @template T, R
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<R>} worker
 * @returns {Promise<R[]>}
 */
async function getTasks(items, concurrency, worker) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function run() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await worker(items[index], index);
    }
  }

  const runners = Array.from({ length: Math.min(concurrency, items.length) }, () => run());
  await Promise.all(runners);
  return results;
}

/**
 * Toggle Liquid-rendered empty state vs product grid.
 *
 * @param {HTMLElement} panel
 * @param {boolean} visible
 */
export function setEmptyVisible(panel, visible) {
  const empty = panel.querySelector('[data-wuu-tabs-empty]');
  const grid = panel.querySelector('[data-wuu-tabs-grid]');

  empty?.toggleAttribute('hidden', !visible);
  grid?.toggleAttribute('hidden', visible);

  // Inline override: section `display: flex` on empty beats UA [hidden] until CSS recompiles.
  if (empty instanceof HTMLElement) {
    empty.style.display = visible ? 'flex' : 'none';
  }
  if (grid instanceof HTMLElement) {
    grid.style.display = visible ? 'none' : '';
  }
}

/**
 * Build the custom API URL.
 * Prefer landing page query (`collection_id`, `tag`, `tags`, `tag_logic`, `p_pid`),
 * then fall back to panel dataset settings.
 *
 * @param {HTMLElement} panel
 * @returns {string}
 */
function getAPI(panel) {
  const endpoint = panel.dataset.apiUrl || DEFAULT_ENDPOINT;
  const params = new URL(window.location.href).searchParams;

  const collectionId = getQueryValue(params, ['collection_id', 'collectionId']) || String(panel.dataset.apiCollectionId || '').trim();
  const productId = getQueryValue(params, ['p_pid']) || String(panel.dataset.apiProductId || '').trim();
  if (!collectionId) return '';

  const page = Number(panel.dataset.nextPage || '1');
  const pageSize = Number(getQueryValue(params, ['page_size', 'pageSize']) || '') || Number(panel.dataset.apiPageSize || '12');
  const url = new URL(endpoint, window.location.origin);

  url.searchParams.set('collection_id', collectionId);
  url.searchParams.set('page', String(page));
  url.searchParams.set('page_size', String(pageSize));
  if (productId) {
    url.searchParams.set('p_pid', productId);
  }

  const tags = getQueryTags(params);
  const presetTags = String(panel.dataset.apiTags || '')
    .split(/[,|，]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
  const resolvedTags = tags.length ? tags : presetTags;

  for (const tag of resolvedTags) {
    url.searchParams.append('tag', tag);
  }

  if (resolvedTags.length) {
    url.searchParams.set('tags', resolvedTags.join(','));
    const tagLogic = (
      getQueryValue(params, ['tag_logic', 'tag_mode']) ||
      panel.dataset.apiTagLogic ||
      'or'
    ).toLowerCase();
    url.searchParams.set('tag_logic', tagLogic === 'and' ? 'and' : 'or');
  }

  return url.toString();
}

/**
 * @param {URLSearchParams} params
 * @param {string[]} keys
 * @returns {string}
 */
function getQueryValue(params, keys) {
  for (const key of keys) {
    const raw = params.get(key);
    if (raw == null) continue;
    const value = String(raw).trim();
    if (value) return value;
  }
  return '';
}

/**
 * Read tags from `tag` (repeated / delimited) and `tags`.
 *
 * @param {URLSearchParams} params
 * @returns {string[]}
 */
function getQueryTags(params) {
  /** @type {string[]} */
  const result = [];

  for (const value of params.getAll('tag')) {
    String(value)
      .split(/[,|，]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => result.push(part));
  }

  const tagsParam = params.get('tags');
  if (tagsParam) {
    String(tagsParam)
      .split(/[,|，]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => result.push(part));
  }

  return [...new Set(result)];
}

/**
 * Prefer data.handles; fall back to data.products[].handle.
 * Sellable filtering belongs on the API — theme only needs handles.
 *
 * @param {object} payload
 * @returns {string[]}
 */
function getHandles(payload) {
  const directs = getValueForKey(payload, 'data.handles');
  if (Array.isArray(directs) && directs.length) {
    return [
      ...new Set(directs.map((value) => String(value || '').trim()).filter(Boolean))
    ];
  }
  const products = getValueForKey(payload, 'data.products');
  if (!Array.isArray(products)) return [];
  const values = products.map((product) => product?.handle)
  return [
    ...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))
  ];
}

/**
 * @param {unknown} source
 * @param {string} path
 * @returns {unknown}
 */
function getValueForKey(source, path) {
  if (!path) return source;
  let cursor = source;
  for (const key of String(path).split('.')) {
    if (!key) continue;
    if (cursor == null || typeof cursor !== 'object' || !(key in cursor)) return undefined;
    cursor = cursor[key];
  }
  return cursor;
}
